/**
 * Prompt Templates Types
 * TypeScript interfaces for prompt template configuration
 */

/** Section type determining data source */
export type SectionType = 'static' | 'hook' | 'dependency' | 'runtime';

/** A section within a prompt template */
export interface PromptSection {
  /** Unique identifier for the section */
  id: string;
  /** Display name for the section */
  name: string;
  /** Section type determining data source */
  type: SectionType;
  /** Sort order (lower = earlier in prompt) */
  order: number;
  /** Whether this section is included in the prompt */
  enabled: boolean;
  /** Data source path (e.g., "config.instructions", "hook.user_facts") */
  source: string;
  /** XML tag to wrap the content */
  wrap_tag: string | null;
  /** Custom template string (optional) */
  template: string | null;
  /** Description of what this section contains */
  description: string | null;
}

/** Request to create a new section */
export interface CreateSectionRequest {
  id: string;
  name: string;
  type: SectionType;
  order: number;
  enabled?: boolean;
  source: string;
  wrap_tag?: string | null;
  template?: string | null;
  description?: string | null;
}

/** Request to update a section */
export interface UpdateSectionRequest {
  name?: string;
  type?: SectionType;
  order?: number;
  enabled?: boolean;
  source?: string;
  wrap_tag?: string | null;
  template?: string | null;
  description?: string | null;
}

/** A prompt template configuration */
export interface PromptTemplate {
  id: number;
  name: string;
  description: string | null;
  separator: string;
  is_default: boolean;
  sections: PromptSection[];
  created_at: string;
  updated_at: string;
}

/** Request to create a new template */
export interface CreateTemplateRequest {
  name: string;
  description?: string | null;
  separator?: string;
  is_default?: boolean;
  sections?: CreateSectionRequest[];
}

/** Request to update a template */
export interface UpdateTemplateRequest {
  name?: string;
  description?: string | null;
  separator?: string;
  is_default?: boolean;
  sections?: CreateSectionRequest[];
}

/** Response for listing templates */
export interface TemplateListResponse {
  templates: PromptTemplate[];
  total: number;
}

/** Section reorder request item */
export interface SectionOrderItem {
  id: string;
  order: number;
}

// =============================================================================
// Live Preview Types (Real Context)
// =============================================================================

/** Request for live prompt preview with real agent context */
export interface LivePreviewRequest {
  agent_type: string;
  user_id?: string;
  user_message?: string;
  session_id?: string;
}

/** Context item from hooks */
export interface ContextItemInfo {
  name: string;
  scope: 'system_prompt' | 'user_message';
  order: number;
  wrap_tag: string | null;
  content_length: number;
  content_preview: string | null;
}

/** Data source info for live preview */
export interface DataSourceInfo {
  source: 'real' | 'empty';
  length?: number;
  reason?: string;
  user_events?: number;
  agent_memories?: number;
}

/** Classifier debug info for live preview */
export interface ClassifierDebugInfo {
  enabled: boolean;
  response_type: string | null;
  confidence: number | null;
  match_layer: string | null;
  skills_enabled: boolean;
  matched_skills: Array<{
    skill_id: string;
    confidence?: number;
    match_layer?: string;
    [key: string]: unknown;
  }>;
  available_skills: Array<{
    skill_id: string;
    name: string;
    description: string;
    enabled: boolean;
  }>;
  skill_tools_registered: boolean;
  injected_instructions: string | null;
}

/** Response for live prompt preview with full context */
export interface LivePreviewResponse {
  system_prompt: string;
  user_message: string;
  agent_type: string;
  user_id: string;
  session_id: string;
  template_name: string | null;
  hooks_executed: {
    system_stage: string[];
    user_stage: string[];
  };
  context_items: ContextItemInfo[];
  data_sources: Record<string, DataSourceInfo>;
  hook_results: Record<string, unknown>;
  classifier: ClassifierDebugInfo | null;
  total_system_prompt_length: number;
  total_user_message_length: number;
}

/** Section type display info */
export const SECTION_TYPE_INFO: Record<SectionType, { label: string; color: string; description: string }> = {
  static: {
    label: 'Static',
    color: 'bg-blue-100 text-blue-800',
    description: 'Fixed content from agent configuration',
  },
  hook: {
    label: 'Hook',
    color: 'bg-green-100 text-green-800',
    description: 'Dynamic content from hooks (user facts, birth chart)',
  },
  dependency: {
    label: 'Dependency',
    color: 'bg-purple-100 text-purple-800',
    description: 'Injected content from dependencies (memory context)',
  },
  runtime: {
    label: 'Runtime',
    color: 'bg-orange-100 text-orange-800',
    description: 'Runtime-generated content (time, output format)',
  },
};

/** Available source options per section type */
export interface SourceOption {
  value: string;
  label: string;
  description: string;
}

export const AVAILABLE_SOURCES: Record<SectionType, SourceOption[]> = {
  static: [
    { value: 'config.description', label: 'Description', description: 'Agent role description' },
    { value: 'config.instructions', label: 'Instructions', description: 'Main agent instructions (+ matched skill instructions)' },
  ],
  hook: [
    { value: 'hook.user_facts', label: 'User Facts', description: 'Permanent user facts from memory' },
    { value: 'hook.birth_chart', label: 'Birth Chart', description: 'User astrological birth chart' },
    { value: 'hook.available_skills', label: 'Available Skills', description: 'List of available skills for the agent' },
    { value: 'hook.skill_instructions', label: 'Skill Instructions', description: 'Matched skill instructions (dynamic)' },
  ],
  dependency: [
    { value: 'dependency.dynamic_system_context', label: 'Dynamic System Context', description: 'Memory context from USER stage hooks' },
  ],
  runtime: [
    { value: 'runtime.time_context', label: 'Time Context', description: 'Current date/time information' },
    { value: 'runtime.output_format', label: 'Output Format', description: 'Output format instructions (JSON, markdown, etc.)' },
  ],
};
